import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  ContributionSchema,
  EventCostsSchema,
  type Contribution,
  type EventCosts,
  type SetEventCostsInput,
} from "@/api/types/contribution";

export const contributionKeys = {
  all: ["contribution"] as const,
  detail: (eventId: string | undefined) => [...contributionKeys.all, eventId] as const,
};

export function useContribution(eventId: string | undefined) {
  return useQuery<Contribution>({
    queryKey: contributionKeys.detail(eventId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/events/${eventId}/contribution`);
      return validateResponse(
        ContributionSchema,
        data,
        "GET /v2/events/:id/contribution",
      );
    },
    enabled: !!eventId,
    staleTime: 10 * 1000,
  });
}

/**
 * PUT /v2/events/:id/costs returns the saved costs row (not the full
 * contribution) — invalidate the contribution query on success so revenue,
 * total costs, and the delta-vs-target all recompute from the new costs.
 */
export function useSetEventCosts() {
  const queryClient = useQueryClient();
  return useMutation<EventCosts, unknown, SetEventCostsInput>({
    mutationFn: async ({ eventId, ...body }) => {
      const { data } = await apiClient.put(`/v2/events/${eventId}/costs`, body);
      return validateResponse(EventCostsSchema, data, "PUT /v2/events/:id/costs");
    },
    onSuccess: (_costs, variables) => {
      void queryClient.invalidateQueries({ queryKey: contributionKeys.detail(variables.eventId) });
    },
  });
}
